import { Badge, Button, cn, InlineText, Spinner } from '@/core/ui';
import {
  ArrowLeft,
  Check,
  ClockCounterClockwise,
  DotsThreeVertical,
  EyeSlash,
  Play,
  RocketLaunch,
  Trash,
  WarningCircle,
} from '@/core/ui/icons';
import type { SaveState } from '../../application/draft';
import { DESCRIPTION_MAX, flowNameProblem, flowStatus, formatDateTime, NAME_MAX } from '../../domain/flow';
import type { Flow } from '../../domain/types';
import { Menu, MenuItem } from '../Menu';

export type BuilderTab = 'fluxo' | 'resultados';

interface Props {
  name: string;
  description: string;
  onMeta: (patch: { name?: string; description?: string }) => void;
  server: Flow;
  saveState: SaveState;
  onRetrySave: () => void;
  tab: BuilderTab;
  onTab: (tab: BuilderTab) => void;
  onBack: () => void;
  onTest: () => void;
  onPublish: () => void;
  publishing: boolean;
  onUnpublish: () => void;
  onDelete: () => void;
}

/** O topo do builder: nome, situação, gravação, versões e as ações do fluxo. */
export function BuilderHeader(p: Props) {
  const live = p.server.live_version !== null;
  const tabs: { id: BuilderTab; label: string }[] = [
    { id: 'fluxo', label: 'Fluxo' },
    { id: 'resultados', label: p.server.sessions ? `Resultados (${p.server.sessions})` : 'Resultados' },
  ];

  return (
    <header className="bg-surface px-4 pt-3 sm:px-6">
      <div className="flex flex-wrap items-start gap-x-4 gap-y-3">
        <button
          type="button"
          onClick={p.onBack}
          aria-label="Voltar para os fluxos"
          title="Fluxos"
          className="mt-0.5 rounded-lg p-1.5 text-fg-subtle hover:bg-surface-2 hover:text-fg"
        >
          <ArrowLeft aria-hidden className="size-4" />
        </button>

        <div className="min-w-0 flex-1 basis-64">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <div className="min-w-0 max-w-full text-lg font-bold text-fg">
              <InlineText
                value={p.name}
                label="nome do fluxo"
                maxLength={NAME_MAX}
                validate={flowNameProblem}
                onSave={async (name) => p.onMeta({ name })}
              />
            </div>
            {flowStatus(p.server).map((s) => (
              <Badge key={s.label} tone={s.tone} className="whitespace-nowrap">
                {s.label}
              </Badge>
            ))}
          </div>
          <div className="mt-0.5 max-w-2xl text-sm text-fg-subtle">
            <InlineText
              value={p.description}
              label="descrição do fluxo"
              placeholder="Adicionar descrição (só o time vê)"
              multiline
              maxLength={DESCRIPTION_MAX}
              onSave={async (description) => p.onMeta({ description })}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <SaveIndicator state={p.saveState} onRetry={p.onRetrySave} />
          <Menu
            label="Versões publicadas"
            kind="dialog"
            trigger={<ClockCounterClockwise aria-hidden className="size-4" />}
            triggerClassName="flex h-10 items-center rounded-lg px-2.5 text-fg-subtle hover:bg-surface-2 hover:text-fg"
            panelClassName="w-72"
          >
            {() => <Versions flow={p.server} />}
          </Menu>
          <Button variant="secondary" onClick={p.onTest}>
            <Play aria-hidden className="size-4" />
            Testar
          </Button>
          <Button onClick={p.onPublish} loading={p.publishing}>
            {!p.publishing && <RocketLaunch aria-hidden className="size-4" />}
            Publicar
          </Button>
          <Menu
            label="Mais ações do fluxo"
            trigger={<DotsThreeVertical aria-hidden className="size-4" />}
            triggerClassName="flex h-10 items-center rounded-lg px-2.5 text-fg-subtle hover:bg-surface-2 hover:text-fg"
          >
            {(close) => (
              <>
                <MenuItem icon={EyeSlash} disabled={!live} onSelect={() => (close(), p.onUnpublish())}>
                  Despublicar
                </MenuItem>
                <MenuItem icon={Trash} onSelect={() => (close(), p.onDelete())}>
                  Excluir fluxo
                </MenuItem>
              </>
            )}
          </Menu>
        </div>
      </div>

      <div role="tablist" aria-label="Fluxo" className="mt-3 flex gap-6">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={p.tab === t.id}
            onClick={() => p.onTab(t.id)}
            className={cn(
              'border-b-2 pb-2.5 text-sm font-medium whitespace-nowrap transition-colors',
              p.tab === t.id ? 'border-brand-400 text-fg' : 'border-transparent text-fg-subtle hover:text-fg',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>
    </header>
  );
}

function SaveIndicator({ state, onRetry }: { state: SaveState; onRetry: () => void }) {
  if (state === 'error')
    return (
      <button
        type="button"
        onClick={onRetry}
        className="flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs text-rose-600 hover:bg-rose-50"
      >
        <WarningCircle aria-hidden className="size-4" />
        Não salvou · tentar de novo
      </button>
    );
  return (
    <span role="status" className="flex min-w-20 items-center gap-1.5 text-xs text-fg-subtle">
      {state === 'saved' ? (
        <>
          <Check aria-hidden className="size-3.5 text-brand-500" />
          Salvo
        </>
      ) : (
        <>
          {state === 'saving' && <Spinner className="size-3.5" />}
          Salvando…
        </>
      )}
    </span>
  );
}

function Versions({ flow }: { flow: Flow }) {
  const versions = [...flow.versions].sort((a, b) => b.number - a.number);
  return (
    <div className="px-4 py-2">
      <p className="pb-2 text-xs font-semibold text-fg-subtle">Versões publicadas</p>
      {versions.length === 0 ? (
        <p className="pb-2 text-sm text-fg-subtle">Nenhuma ainda. Publicar cria a versão 1.</p>
      ) : (
        <ol className="max-h-64 space-y-1 overflow-y-auto pb-1">
          {versions.map((v) => (
            <li key={v.number} className="flex items-center gap-2 text-sm">
              <span className="w-8 font-medium text-fg">v{v.number}</span>
              <span className="flex-1 text-xs text-fg-subtle">{formatDateTime(v.published_at)}</span>
              {v.live && <Badge tone="success">No ar</Badge>}
            </li>
          ))}
        </ol>
      )}
      <p className="pt-1 text-[11px] text-fg-subtle">
        Conversas em andamento seguem na versão em que começaram.
      </p>
    </div>
  );
}
